import { useCallback, useEffect, useRef, useState } from 'react';
import { OperationMS, AnalyseIaStatut } from '../types';
import { getAnalyseIaStatut, lancerAnalyseIa } from '../services/operationMsService';

const POLL_MS = 8000;
const MAX_POLLS = 80; // ~640 s : l'analyse est sequentielle et Claude Code est lent (~1-2 min/offre).

const isTerminal = (s: AnalyseIaStatut): boolean =>
  s === 'terminee' || s === 'terminee_partielle' || s === 'erreur';

// Pilote l'analyse IA des offres (statut 4). Asynchrone : on lance le job (202) puis on
// poll le statut. A l'etat terminal, on refetch -> le formulaire se re-seme depuis les
// note_technique desormais pre-remplies (useNotesTechniques). Reprend le polling si on
// arrive avec une analyse deja en cours. Nettoyage du timer au demontage.
export const useAnalyseIa = (operation: OperationMS, refetch: () => void) => {
  const [etat, setEtat] = useState<AnalyseIaStatut>(operation.analyse_ia_statut ?? 'none');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollsRef = useRef(0);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(
    (operationId: number | string) => {
      stop();
      pollsRef.current = 0;
      timerRef.current = setInterval(async () => {
        pollsRef.current += 1;
        try {
          const res = await getAnalyseIaStatut(operationId);
          const statut = res.data?.statut ?? 'none';
          if (isTerminal(statut)) {
            stop();
            setEtat(statut);
            refetch();
            return;
          }
          setEtat('en_cours');
        } catch {
          // Erreur de poll ponctuelle : on retente au tick suivant.
        }
        if (pollsRef.current >= MAX_POLLS) {
          stop();
          setEtat('erreur');
        }
      }, POLL_MS);
    },
    [refetch, stop],
  );

  const lancer = useCallback(
    async (force = false) => {
      setEtat('en_cours');
      try {
        await lancerAnalyseIa(operation.id, force);
        poll(operation.id);
      } catch {
        setEtat('erreur');
      }
    },
    [operation.id, poll],
  );

  // Reprise si l'analyse etait deja en cours a l'arrivee ; cleanup au demontage / changement d'op.
  useEffect(() => {
    if (operation.analyse_ia_statut === 'en_cours') {
      poll(operation.id);
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation.id]);

  return { etat, lancer };
};
